import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';

/**
 * Custom hook for managing payment transactions
 *
 * UPDATED: Now uses apiService with request cancellation
 * - Removed redundant Authorization headers
 * - Added request cancellation for fetchTransactions
 */
export const usePaymentTransactions = (API) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    payment_gateway: '',
    status: '',
    search: '',
    date_from: '',
    date_to: '',
    limit: 100
  });

  /**
   * Fetch transactions with filters
   * Now includes request cancellation
   */
  const fetchTransactions = useCallback(async () => {
    const controller = new AbortController();

    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (filters.limit) params.append('limit', filters.limit);
      if (filters.payment_gateway) params.append('payment_gateway', filters.payment_gateway);
      if (filters.status) params.append('status', filters.status);

      const response = await apiService.get(`/admin/payment-transactions?${params.toString()}`, {
        signal: controller.signal
      });

      let filteredTransactions = response.data;

      // Client-side filtering for search
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredTransactions = filteredTransactions.filter(t =>
          t.booking_id?.toLowerCase().includes(searchLower) ||
          t.order_id?.toLowerCase().includes(searchLower) ||
          t.booking_info?.guest_name?.toLowerCase().includes(searchLower) ||
          t.booking_info?.guest_email?.toLowerCase().includes(searchLower)
        );
      }

      // Client-side filtering for date range
      if (filters.date_from) {
        filteredTransactions = filteredTransactions.filter(t =>
          new Date(t.created_at) >= new Date(filters.date_from)
        );
      }
      if (filters.date_to) {
        const dateTo = new Date(filters.date_to);
        dateTo.setHours(23, 59, 59, 999); // End of day
        filteredTransactions = filteredTransactions.filter(t =>
          new Date(t.created_at) <= dateTo
        );
      }

      setTransactions(filteredTransactions);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      console.error('Error fetching transactions:', err);
      toast.error(err.response?.data?.detail || 'Failed to fetch transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, [filters]);

  /**
   * Fetch with cleanup on filter changes
   */
  useEffect(() => {
    const cleanup = fetchTransactions();
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fetchTransactions]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Clear filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      payment_gateway: '',
      status: '',
      search: '',
      date_from: '',
      date_to: '',
      limit: 100
    });
  }, []);

  /**
   * Export to CSV
   */
  const exportToCSV = useCallback(() => {
    if (transactions.length === 0) {
      toast.error('No transactions to export');
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Transaction ID',
      'Booking ID',
      'Order ID',
      'Gateway',
      'Type',
      'Amount',
      'Currency',
      'Status',
      'Guest Name',
      'Guest Email',
      'Check In',
      'Check Out',
      'Created At'
    ];

    // Prepare CSV rows
    const rows = transactions.map(t => [
      t.id || '',
      t.booking_id || '',
      t.order_id || '',
      t.payment_gateway || '',
      t.payment_type || '',
      t.amount || '',
      t.currency || '',
      t.status || '',
      t.booking_info?.guest_name || '',
      t.booking_info?.guest_email || '',
      t.booking_info?.check_in || '',
      t.booking_info?.check_out || '',
      t.created_at || ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payment_transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${transactions.length} transactions`);
  }, [transactions]);

  return {
    transactions,
    loading,
    filters,
    updateFilters,
    clearFilters,
    fetchTransactions,
    exportToCSV
  };
};
